import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useUser } from "@clerk/clerk-react";
import { axiosInstance } from "@/lib/axios-instance";

export interface CompanyFileId {
  fileType: string;
  fileId: string;
}

export interface Company {
  _id: string;
  company_name: string;
  short_bio?: string;
  country?: string;
  city?: string;
  province?: string;
  industry?: string[];
  company_website?: string;
  linkedIn?: string;
  address?: string;
  company_file_ids?: CompanyFileId[];
  company_source?: string;
  stage?: number;
  created_on?: string;
  deal_radar_highlight?: boolean;
  deal_radar_order?: number;
}

interface Pagination {
  total: number;
  page: number;
  pages: number;
  limit: number;
}

interface DealRadarResponse {
  companies: Company[];
  pagination: Pagination;
}

interface FilterValues {
  countries?: string[];
  industries?: string[];
  sources?: string[];
}

export function useCreateOpportunityApplication() {
  const { activeWorkspace } = useWorkspaceStore();
  const { isLoaded, isSignedIn, user } = useUser();

  return useMutation({
    mutationFn: async (companyId: string) => {
      if (!activeWorkspace?._id) {
        throw new Error("No active workspace selected");
      }

      if (!isLoaded || !isSignedIn || !user?.id) {
        throw new Error("No active workspace or user");
      }

      const { data } = await axiosInstance.post(
        "/create_opportunity_application_explore",
        {
          applicant_company: companyId,
          pertains_to_opportunity: activeWorkspace._id,
          changed_by: user.id,
          source: "DealRadar",
        }
      );

      return data;
    },
    // onSuccess and onError removed to allow caller control
  });
}

export function useDealRadar(
  page: number = 1,
  limit: number = 10,
  filters: FilterValues = {},
  searchQuery: string = "",
  options: { enabled?: boolean } = {}
) {
  const { activeWorkspace } = useWorkspaceStore();

  return useQuery<DealRadarResponse>({
    queryKey: [
      "deal-radar",
      page,
      limit,
      filters,
      searchQuery,
      activeWorkspace?._id,
    ],
    queryFn: async () => {
      if (!activeWorkspace?._id) {
        throw new Error("No active workspace");
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        opportunity_id: activeWorkspace._id,
        ...(filters.countries?.length && {
          countries: filters.countries.join(","),
        }),
        ...(filters.industries?.length && {
          industries: filters.industries.join(","),
        }),
        ...(searchQuery && {
          search: searchQuery,
        }),
        ...(filters.sources?.length && {
          sources: filters.sources.join(","),
        }),
      });

      const { data } = await axiosInstance.get(
        `/fetch_companies_for_deal_radar?${params}`
      );
      return data;
    },
    enabled: !!activeWorkspace?._id && (options.enabled ?? true),
  });
}

export function useInfiniteDealRadar(
  limit: number = 10,
  filters: FilterValues = {},
  searchQuery: string = "",
  options: { enabled?: boolean } = {}
) {
  const { activeWorkspace } = useWorkspaceStore();

  return useInfiniteQuery({
    queryKey: [
      "deal-radar-infinite",
      limit,
      filters,
      searchQuery,
      activeWorkspace?._id,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      if (!activeWorkspace?._id) {
        throw new Error("No active workspace");
      }

      const params = new URLSearchParams({
        page: pageParam.toString(),
        limit: limit.toString(),
        opportunity_id: activeWorkspace._id,
        ...(filters.countries?.length && {
          countries: filters.countries.join(","),
        }),
        ...(filters.industries?.length && {
          industries: filters.industries.join(","),
        }),
        ...(searchQuery && {
          search: searchQuery,
        }),
        ...(filters.sources?.length && {
          sources: filters.sources.join(","),
        }),
      });

      const { data } = await axiosInstance.get(
        `/fetch_companies_for_deal_radar?${params}`
      );
      return data as DealRadarResponse;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.pages) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!activeWorkspace?._id && (options.enabled ?? true),
  });
}
